import { FCC, isSuccess, useAnalytics, useFormatDate, useFormatDateTime, useLocale, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { differenceInDays } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
	RegistrationCanceledAnalyticsEvent,
	RegistrationDeclinedAnalyticsEvent,
	RegistrationReplacementAnalyticsEvent,
} from "../../analytics/analyticsEvents";
import { useRegion } from "../../region/RegionProvider";
import { registrationFormService, useLandingPageConsents } from "../../shared/api/registrationForms";
import { travelSegmentService } from "../../shared/api/travelSegmentService";
import { useHotelSummary } from "../../shared/api/useHotelSummary";
import { useConfig } from "../../shared/hooks/useConfig";
import { useIndexedDb } from "../../shared/storage/IndexedDbProvider";
import {
	CompanionsByType,
	DataFieldValues,
	RegistrationFormResource,
	RegistrationFormSectionResourceTypeCompanionSection,
	RegistrationFormSectionResourceTypeHotelSection,
	RegistrationFormStaticStepResource,
	RegistrationFormStepChildrenResourceEntityTypeSection,
	RegistrationFormStepResource,
	RegistrationHotelStayRequest,
	RegistrationStatus,
	RegistrationTravelRequest,
	SubmitRegistrationRequest,
} from "../../shared/types";
import { HotelSectionFormValues, HotelStayFormValues, StayConfigurationResource } from "../../shared/types/hotel";
import { GuestTravelFormValues } from "../../shared/types/travel";
import { useSessionState } from "../../shared/utils/useSessionState";
import { FILLED_STEPS } from "../RegistrationFormAvailablePage";
import { isExistingFile } from "../RegistrationFormFileField";
import {
	findNextStep,
	findPrevStep,
	flattenStepDataFields,
	getDataFieldsVisibility,
	getSectionsFromStep,
	getSectionsVisibility,
	getStepsVisibility,
	isSectionHidden,
	mapConsentValuesToBody,
	toGuestAttributeChildrenFromSection,
} from "../registrationFormUtils";
import { getDataFieldValidationErrorMessage, validateDataField } from "../rules/dataField";
import { mapGuestTravelToRegistrationTravelRequest } from "../section/travel/mapGuestTravelToRegistrationTravelRequest";
import { TravelValidationResult, validateTravels } from "../section/travel/validateTravels";
import { validateCompanionSection } from "../section/validateCompanionSection";
import {
	CompanionErrors,
	ConsentValues,
	CurrentCompanion,
	DataFieldErrors,
	FilledSteps,
	HotelSectionValidationResult,
	IRegistrationSubmitValues,
	RegistrationFormContext,
} from "./registrationFormProviderContext";
import {
	toCompanionDataFieldValues,
	toDataField,
	toDataFieldRequestValues,
	toDataFieldTypeValidationErrors,
} from "./registrationFormProviderUtils";

interface RegistrationFormProviderProps {
	registrationForm: RegistrationFormResource;
	hasFileField: boolean;
	initialFilledSteps: FilledSteps;
}

export const RegistrationFormProvider: FCC<RegistrationFormProviderProps> = ({
	registrationForm,
	initialFilledSteps,
	hasFileField,
	children,
}) => {
	const { track } = useAnalytics();
	const { productData } = useConfig();
	const navigate = useNavigate();
	const { store, clearObjectStore } = useIndexedDb();
	const formatDate = useFormatDate();
	const formatDateTime = useFormatDateTime();
	const { formatLocale } = useLocale();
	const queryClient = useQueryClient();
	const { regionId } = useRegion();

	const [filledSteps, setFilledSteps] = useState<FilledSteps>(initialFilledSteps);
	const [givenConsents, setGivenConsents] = useSessionState<ConsentValues>("givenConsents", {});
	const [hasSeenLanding, setHasSeenLanding] = useSessionState<boolean>("hasSeenLanding", false);

	const [companionsByType, setCompanionsByType] = useSessionState<CompanionsByType[]>(
		"companions",
		registrationForm.guest.companionsByType,
	);
	const [travels, setTravels] = useSessionState<GuestTravelFormValues[]>(
		"travels",
		registrationForm.guest.travels.map((resource) => ({
			...resource,
			arrivalTravelTemplate: null,
			departureTravelTemplate: null,
		})),
	);
	const { landingConsents, isLoading: areConsentsLoading } = useLandingPageConsents(
		registrationForm.id,
		regionId ?? null,
	);

	const [errors, setErrors] = useState<Record<string, string>>({});

	const { t } = useTranslation();
	const { showToast } = useToasts();

	const storeGuestFile = useCallback(
		async (value: string | File | null) => {
			if (!hasFileField || !value || isExistingFile(value)) {
				return;
			} else {
				return await store("guest_files", value);
			}
		},
		[hasFileField, store],
	);

	const hiddenSteps = useMemo(
		() => getStepsVisibility(registrationForm.ruleSets, filledSteps, registrationForm.guest, registrationForm.steps),
		[filledSteps, registrationForm.guest, registrationForm.ruleSets, registrationForm.steps],
	);

	const _getHiddenSections = useCallback(
		(filledSteps: FilledSteps) =>
			getSectionsVisibility(registrationForm.steps, registrationForm.ruleSets, filledSteps, registrationForm.guest),
		[registrationForm.guest, registrationForm.ruleSets, registrationForm.steps],
	);

	const _getHiddenDataFields = useCallback(
		(filledSteps: FilledSteps) =>
			getDataFieldsVisibility(registrationForm.steps, registrationForm.ruleSets, filledSteps, registrationForm.guest),
		[registrationForm.guest, registrationForm.ruleSets, registrationForm.steps],
	);

	const submitStep = useCallback(
		async (stepId: number, values: DataFieldValues) => {
			const newFilledSteps = {
				...filledSteps,
				[stepId]: values,
			};
			const hiddenSteps = getStepsVisibility(
				registrationForm.ruleSets,
				newFilledSteps,
				registrationForm.guest,
				registrationForm.steps,
			);

			const sessionStorageValues = { ...values };

			for (const dataFieldId of Object.keys(values)) {
				if (values[dataFieldId] instanceof File) {
					try {
						sessionStorage.setItem("isActiveSession", "true");
						sessionStorageValues[dataFieldId] = await storeGuestFile(values[dataFieldId]);
					} catch {
						// File Blob wurde nicht in den indexDB geschrieben (z.B. privater Tab in Safari)
						// Wert wird entfernt, sodass beim herausladen (z.B. Page refresh) das Datenfeld einfach leer bleibt (und ggf. neu ausgefüllt werden muss)
						sessionStorageValues[dataFieldId] = null;
					}
				}
			}
			setFilledSteps(newFilledSteps);

			const filledStepsForSessionStorage = {
				...filledSteps,
				[stepId]: sessionStorageValues,
			};
			sessionStorage.setItem(FILLED_STEPS, JSON.stringify(filledStepsForSessionStorage));
			setErrors({});
			void queryClient.resetQueries({ queryKey: ["shop-summary"] });
			return findNextStep(hiddenSteps, registrationForm.steps, stepId);
		},
		[
			filledSteps,
			queryClient,
			registrationForm.guest,
			registrationForm.ruleSets,
			registrationForm.steps,
			storeGuestFile,
		],
	);

	const validateCompanionValues = useCallback(
		(
			stepId: number,
			stepFormValues: Record<number, any>,
			section: RegistrationFormSectionResourceTypeCompanionSection,
			companionValues: DataFieldValues,
		): DataFieldErrors => {
			const filledStepsWithCompanionValues: FilledSteps = {
				...filledSteps,
				[stepId]: { ...stepFormValues, ...companionValues },
			};
			const hiddenDataFields = getDataFieldsVisibility(
				registrationForm.steps,
				registrationForm.ruleSets,
				filledStepsWithCompanionValues,
				registrationForm.guest,
			);

			const visibleDataFields = toGuestAttributeChildrenFromSection(section)
				.filter((attribute) => !hiddenDataFields.includes(`${section.id}-${attribute.dataField.id}`))
				.map(({ dataField }) => dataField);

			const dataFieldValidationRuleErrors = section.validationRules
				.filter(
					(rule) =>
						!hiddenDataFields.includes(`${section.id}-${rule.guestAttribute.dataField.id}`) &&
						!validateDataField(rule, companionValues, false),
				)
				.reduce((acc, rule) => {
					const message = getDataFieldValidationErrorMessage(
						{
							t,
							formatDate,
							formatDateTime,
						},
						rule,
						companionValues,
					);
					return {
						...acc,
						[rule.guestAttribute.dataField.id]: message,
					};
				}, {} as DataFieldErrors);

			return visibleDataFields.reduce(
				toDataFieldTypeValidationErrors(companionValues, t),
				dataFieldValidationRuleErrors,
			);
		},
		[
			filledSteps,
			formatDate,
			formatDateTime,
			registrationForm.guest,
			registrationForm.ruleSets,
			registrationForm.steps,
			t,
		],
	);

	const companionFormValidation = useCallback(
		(
			stepId: number,
			stepFormValues: Record<number, any>,
			section: RegistrationFormSectionResourceTypeCompanionSection,
			values: Record<number, any>,
		) => {
			const companionValues = Object.fromEntries(
				Object.entries(values).map(([key, value]) => [key.replace("companion_", ""), value]),
			);

			const errors = validateCompanionValues(stepId, stepFormValues, section, companionValues);

			return Object.fromEntries(Object.entries(errors).map(([key, message]) => [`companion_${key}`, message]));
		},
		[validateCompanionValues],
	);

	const validateCompanions = useCallback(
		(
			stepId: number,
			stepFormValues: Record<number, any>,
			section: RegistrationFormSectionResourceTypeCompanionSection,
		): CompanionErrors => {
			const companions =
				companionsByType.find((type) => type.id === section.companionSettings.companionType.id)?.companions ?? [];

			return companions.reduce((companionErrors, companion, index) => {
				const errors = validateCompanionValues(stepId, stepFormValues, section, toCompanionDataFieldValues(companion));

				return Object.keys(errors).length > 0 ? { ...companionErrors, [index]: errors } : companionErrors;
			}, {} as CompanionErrors);
		},
		[companionsByType, validateCompanionValues],
	);

	const validateStep = useCallback(
		(step: RegistrationFormStepResource, values: DataFieldValues) => {
			const allFilledSteps = {
				...filledSteps,
				[step.id]: values,
			};
			const hiddenDataFields = getDataFieldsVisibility(
				[step],
				registrationForm.ruleSets,
				allFilledSteps,
				registrationForm.guest,
			);

			const dataFieldErrors: DataFieldErrors = getSectionsFromStep(step)
				.flatMap(({ section }) => toGuestAttributeChildrenFromSection(section))
				.map(toDataField)
				.reduce(toDataFieldTypeValidationErrors(values, t), {});

			const errors: DataFieldErrors = step.children
				.flatMap((x) => {
					if (x.entityType === "section" && x.section.type === "default") {
						return x.section.validationRules.filter((rule) => {
							return (
								!hiddenDataFields.includes(`${x.section.id}-${rule.guestAttribute.dataField.id}`) &&
								!validateDataField(rule, values, false)
							);
						});
					}
					return [];
				})
				.reduce((errors, rule) => {
					const message = getDataFieldValidationErrorMessage({ t, formatDate, formatDateTime }, rule, values);
					return {
						...errors,
						[rule.guestAttribute.dataField.id]: message,
					};
				}, {} as DataFieldValues);

			const needsToValidateTravelSection =
				!isSectionHidden(registrationForm, "travel", allFilledSteps) &&
				step.children.some((child) => child.entityType === "section" && child.section.type === "travel");

			let travelErrors: TravelValidationResult | null = {};
			if (needsToValidateTravelSection && registrationForm.travelSectionSettings) {
				travelErrors = validateTravels(registrationForm.travelSectionSettings, travels, t);
			}
			const companionSections = step.children.filter(
				(child) =>
					child.entityType === "section" &&
					child.section.type === "companion" &&
					!_getHiddenSections({
						...filledSteps,
						[step.id]: values,
					}).includes(child.section.id),
			);

			let companionSectionErrors: Record<number, string> | null = {};
			companionSections.forEach((companionSection) => {
				const section = (companionSection as RegistrationFormStepChildrenResourceEntityTypeSection)
					.section as RegistrationFormSectionResourceTypeCompanionSection;
				const companionType = section.companionSettings.companionType;
				const companionCount = companionsByType.find((type) => type.id === companionType.id)?.companions.length ?? 0;
				const countError = validateCompanionSection(section, companionCount, t);

				if (countError) {
					companionSectionErrors = { ...companionSectionErrors, ...countError };
					return;
				}

				if (Object.keys(validateCompanions(step.id, values, section)).length > 0) {
					companionSectionErrors = {
						...companionSectionErrors,
						[section.id]: t("Please complete the data of all {{companions}}", {
							companions: companionType.pluralLabel,
						}),
					};
				}
			});

			return { ...errors, ...dataFieldErrors, ...travelErrors, ...companionSectionErrors };
		},
		[
			_getHiddenSections,
			companionsByType,
			filledSteps,
			formatDate,
			formatDateTime,
			registrationForm,
			t,
			travels,
			validateCompanions,
		],
	);

	const validateStaticStep = useCallback(
		(step: RegistrationFormStaticStepResource, values: DataFieldValues) => {
			const dataFieldErrors: DataFieldErrors = getSectionsFromStep(step)
				.flatMap(({ section }) => toGuestAttributeChildrenFromSection(section))
				.map(toDataField)
				.reduce(toDataFieldTypeValidationErrors(values, t), {});

			const errors: DataFieldErrors = step.children
				.flatMap((x) => {
					if (x.entityType === "section" && x.section.type === "default") {
						return x.section.validationRules.filter((rule) => {
							return !validateDataField(rule, values, false);
						});
					}
					return [];
				})
				.reduce((errors, rule) => {
					const message = getDataFieldValidationErrorMessage({ t, formatDate, formatDateTime }, rule, values);
					return {
						...errors,
						[rule.guestAttribute.dataField.id]: message,
					};
				}, {} as DataFieldValues);

			return { ...errors, ...dataFieldErrors };
		},
		[formatDate, formatDateTime, t],
	);

	const clearRegistration = useCallback(async () => {
		if (hasFileField) {
			await clearObjectStore("guest_files");
		}
		setGivenConsents({});
		setFilledSteps([]);
		setCompanionsByType([]);
		setErrors({});
		sessionStorage.clear();
	}, [clearObjectStore, hasFileField, setCompanionsByType, setGivenConsents]);

	const createSubmitRegistrationRequest = useCallback(
		({ summaryPageConsents, shopSummaryHash, paymentProviderId }: IRegistrationSubmitValues) => {
			const formData = new FormData();
			let dataFieldValues = getDataFieldValuesToSubmit(registrationForm, filledSteps, formData);
			const allConsents = mapConsentValuesToBody(givenConsents).concat(mapConsentValuesToBody(summaryPageConsents));

			if (productData.enableAdobeAnalytics && productData.adobeAnalyticsStcCodeDataFieldId) {
				const stcCodeValue = {
					id: productData.adobeAnalyticsStcCodeDataFieldId,
					value: (window as any).ste_getVisitReferrer ? (window as any).ste_getVisitReferrer() : null,
				};
				dataFieldValues = [...dataFieldValues, stcCodeValue];
			}

			const travelRequests: RegistrationTravelRequest[] = isSectionHidden(registrationForm, "travel", filledSteps)
				? []
				: travels.map(mapGuestTravelToRegistrationTravelRequest);
			const hotelSectionsToSubmit = getHotelSectionsToSubmit(registrationForm, filledSteps);

			const body: SubmitRegistrationRequest = {
				dataFieldValues,
				consents: allConsents,
				companions: companionsByType.flatMap((typeGroup) => typeGroup.companions),
				travels: travelRequests,
				shopSummaryHash,
				paymentProviderId,
				token: registrationForm.guest.id === "new" ? undefined : registrationForm.guest.id,
				registrationLocale: formatLocale,
				hotelStays: hotelSectionsToSubmit.flatMap(({ section, formValues }) => {
					return formValues.hotelStays.map(
						(formValue): RegistrationHotelStayRequest => ({
							id: formValue.id,
							hotelStayTypeId: section.hotelSettings.hotelStayTypeId,
							checkIn: formValue.checkIn,
							checkOut: formValue.checkOut,
							requestedHotelId: formValue.requestedHotelId,
							requestedRoomTypeId: formValue.requestedRoomTypeId,
							remarkByGuest: formValue.remarkByGuest,
							additionalPersonsText: formValue.additionalPersonsText,
							additionalPersonsEnabled: formValue.additionalPersonsEnabled,
							cancellationRequested: formValue.cancellationRequested || false,
						}),
					);
				}),
				regionId: regionId,
			};

			formData.append("registration", JSON.stringify(body));

			return formData;
		},
		[
			companionsByType,
			filledSteps,
			formatLocale,
			givenConsents,
			productData.adobeAnalyticsStcCodeDataFieldId,
			productData.enableAdobeAnalytics,
			regionId,
			registrationForm,
			travels,
		],
	);

	const submitRegistrationFormDataForShopSummary = useCallback(
		async (values: IRegistrationSubmitValues) => {
			const formData = createSubmitRegistrationRequest(values);

			const response = await registrationFormService.submitRegistrationFormDataForShopSummary(
				registrationForm.id,
				formData,
			);

			return response.data.data;
		},
		[createSubmitRegistrationRequest, registrationForm.id],
	);

	const submitRegistration = useCallback(
		async (values: IRegistrationSubmitValues) => {
			const formData = createSubmitRegistrationRequest(values);

			const response = await registrationFormService.submitRegistrationFormData(registrationForm.id, formData);

			if (axios.isAxiosError(response)) {
				void queryClient.invalidateQueries({
					queryKey: travelSegmentService.baseKey,
				});
				if (response.response?.data.travelSegmentId) {
					setTravels(
						travels.filter(
							(i) =>
								i.arrivals.every((arrival) => arrival.id !== response.response?.data.travelSegmentId) &&
								i.departures.every((departure) => departure.id !== response.response?.data.travelSegmentId),
						),
					);
				}
				setErrors(response.response?.data.errors || {});
			} else {
				setErrors({});
			}

			if (isSuccess(response)) {
				switch (response.data.data.status) {
					case RegistrationStatus.REGISTERED:
						navigate("../success");
						break;
					case RegistrationStatus.REGISTERED_WITH_PAYMENT_PROVIDER:
						window.location.href = response.data.data.paymentLink;
						break;
					case RegistrationStatus.REGISTERED_WITH_POSITIVE_SCREENING:
					case RegistrationStatus.WAITING_LIST_WITH_POSITIVE_SCREENING:
					case RegistrationStatus.REGISTRATION_CHANGED_WITH_POSITIVE_SCREENING:
						navigate("../confirmed");
						break;
					case RegistrationStatus.REGISTRATION_CHANGED:
						navigate("../edited");
						break;
					case RegistrationStatus.WAITING_LIST:
						if (registrationForm.waitingListConfirmationBlockId) {
							navigate(`../waiting-list`);
						} else {
							showToast({
								body: t("You were placed on the waiting list."),
								title: t("Registration"),
								type: "success",
							});
						}
				}

				return true;
			}

			return false;
		},
		[
			createSubmitRegistrationRequest,
			registrationForm.id,
			registrationForm.waitingListConfirmationBlockId,
			travels,
			queryClient,
			setTravels,
			navigate,
			showToast,
			t,
		],
	);

	const declineRegistration = useCallback(
		async (dataFieldValues: DataFieldValues) => {
			const response = await registrationFormService.declineRegistration(
				registrationForm.id,
				Object.keys(dataFieldValues).map((id) => ({
					id: id as unknown as number,
					value: dataFieldValues[id as any],
				})),
				formatLocale,
				registrationForm.guest.id === "new" && regionId ? regionId : null,
				registrationForm.guest.id === "new" ? undefined : registrationForm.guest.id,
			);
			if (isSuccess(response)) {
				navigate("../declined", { replace: true });
				track(new RegistrationDeclinedAnalyticsEvent(registrationForm.id, new Date()));
			} else {
				showToast({ body: t("An error has occurred"), type: "error", title: t("Declination") });
			}
		},
		[formatLocale, navigate, regionId, registrationForm.guest.id, registrationForm.id, showToast, t, track],
	);

	const cancelRegistration = useCallback(
		async (dataFieldValues: DataFieldValues) => {
			const response = await registrationFormService.cancelRegistration(
				registrationForm.id,
				Object.keys(dataFieldValues).map((id) => ({
					id: id as unknown as number,
					value: dataFieldValues[id as any],
				})),
				formatLocale,
				registrationForm.guest.id === "new" && regionId ? regionId : null,
				registrationForm.guest.id === "new" ? undefined : registrationForm.guest.id,
			);
			if (isSuccess(response)) {
				navigate("../cancelled", { replace: true });
				track(new RegistrationCanceledAnalyticsEvent(registrationForm.id, new Date()));
			} else {
				showToast({ body: t("An error has occurred"), type: "error", title: t("Cancellation") });
			}
		},
		[formatLocale, navigate, regionId, registrationForm.guest.id, registrationForm.id, showToast, t, track],
	);

	const nominateReplacement = useCallback(
		async (dataFieldValues: DataFieldValues) => {
			const response = await registrationFormService.nominateReplacement(
				registrationForm.id,
				Object.keys(dataFieldValues).map((id) => ({
					id: id as unknown as number,
					value: dataFieldValues[id as any],
				})),
				registrationForm.guest.id === "new" ? undefined : registrationForm.guest.id,
			);
			if (isSuccess(response)) {
				navigate("../replaced", { replace: true });
				track(new RegistrationReplacementAnalyticsEvent(registrationForm.id, new Date()));
			} else {
				showToast({ body: t("An error has occurred"), type: "error", title: t("Nominate replacement") });
			}
		},
		[navigate, registrationForm, showToast, t, track],
	);

	const previousStep = (currentStepId?: number) => {
		const hiddenSteps = getStepsVisibility(
			registrationForm.ruleSets,
			filledSteps,
			registrationForm.guest,
			registrationForm.steps,
		);
		return findPrevStep(hiddenSteps, registrationForm.steps, currentStepId ?? null);
	};

	const submitLanding = useCallback(
		(consents: ConsentValues) => {
			const hiddenSteps = getStepsVisibility(
				registrationForm.ruleSets,
				filledSteps,
				registrationForm.guest,
				registrationForm.steps,
			);
			const newConsents = { ...givenConsents, ...consents };
			setGivenConsents(newConsents);
			setHasSeenLanding(true);

			return findNextStep(hiddenSteps, registrationForm.steps, null);
		},
		[
			filledSteps,
			givenConsents,
			registrationForm.guest,
			registrationForm.ruleSets,
			registrationForm.steps,
			setGivenConsents,
			setHasSeenLanding,
		],
	);

	const saveCompanion = useCallback(
		(
			currentCompanion: CurrentCompanion,
			setError: (error: Record<number, string> | null) => void,
			section: RegistrationFormSectionResourceTypeCompanionSection,
		) => {
			if (companionsByType.find((c) => c.id === currentCompanion.companionType.id) === undefined) {
				companionsByType.push({
					id: currentCompanion.companionType.id,
					singularLabel: currentCompanion.companionType.singularLabel,
					pluralLabel: currentCompanion.companionType.pluralLabel,
					companions: [currentCompanion.companion],
				});
				setCompanionsByType(companionsByType);
				setError(
					validateCompanionSection(
						section,
						companionsByType.find((c) => c.id === currentCompanion.companionType.id)?.companions.length ?? 0,
						t,
					),
				);

				return;
			}

			const updatedCompanions = companionsByType.map((group) => {
				if (group.id === currentCompanion.companionType.id) {
					const newCompanions = group.companions.map((c, index) =>
						index === currentCompanion.index ? currentCompanion.companion : c,
					);

					const orderedNewCompanions =
						group.companions.findIndex((_, index) => currentCompanion.index === index) >= 0
							? newCompanions
							: newCompanions.concat(currentCompanion.companion);

					return { ...group, companions: orderedNewCompanions };
				}

				return group;
			});

			setCompanionsByType(updatedCompanions);
			setError(
				validateCompanionSection(
					section,
					updatedCompanions.find((c) => c.id === currentCompanion.companionType.id)?.companions.length ?? 0,
					t,
				),
			);
		},
		[companionsByType, setCompanionsByType, t],
	);

	const removeCompanion = useCallback(
		(
			indexToRemove: number,
			companionTypeId: number,
			setError: (error: Record<number, string> | null) => void,
			section: RegistrationFormSectionResourceTypeCompanionSection,
		) => {
			const updatedCompanions = companionsByType.map((group) => {
				if (group.id === companionTypeId) {
					const newCompanions = group.companions.filter((c, index) => index !== indexToRemove);
					return { ...group, companions: newCompanions };
				}
				return group;
			});

			setCompanionsByType(updatedCompanions);
			setError(
				validateCompanionSection(
					section,
					updatedCompanions.find((c) => c.id === section.companionSettings.companionType.id)?.companions.length ?? 0,
					t,
				),
			);
		},
		[companionsByType, setCompanionsByType, t],
	);

	const getStayConfigurationValidation =
		(stayConfiguration: StayConfigurationResource) =>
		(values: HotelSectionFormValues | null): HotelSectionValidationResult | undefined => {
			if (
				stayConfiguration.minStayPeriods !== 0 &&
				(!values?.hotelStays || values.hotelStays.length < stayConfiguration.minStayPeriods)
			) {
				return {
					count: t("At least {{count}} hotel stays must be specified.", { count: stayConfiguration.minStayPeriods }),
				};
			}
		};

	const getHotelStayValidation =
		(stayConfiguration: StayConfigurationResource, otherStays: HotelStayFormValues[]) =>
		(values: HotelStayFormValues | null) => {
			if (!values) {
				return;
			}

			if (!values.checkIn || !values.checkOut) {
				return t("The Check-in and check-out field is required.");
			}

			if (values.checkIn >= values.checkOut) {
				return t("Check-in date must be before check-out date.");
			}

			if (values.checkIn < stayConfiguration.earliestCombinedCheckIn) {
				return t("Check-in date must be after {{date}}.", {
					date: formatDate(stayConfiguration.earliestCombinedCheckIn),
				});
			}

			if (values.checkOut > stayConfiguration.latestCombinedCheckOut) {
				return t("Check-out date must be before {{date}}.", {
					date: formatDate(stayConfiguration.latestCombinedCheckOut),
				});
			}

			if (values.mainStay) {
				if (values.checkIn < values.mainStay.checkIn) {
					return t("Check-in date must be after {{date}}.", {
						date: formatDate(values.mainStay.checkIn),
					});
				}

				if (values.checkOut > values.mainStay.checkOut) {
					return t("Check-out date must be before {{date}}.", {
						date: formatDate(values.mainStay.checkOut),
					});
				}
			}

			if (values.additionalPersonsEnabled && !values.additionalPersonsText) {
				return t("The additional person field must be filled.");
			}

			if (otherStays.length > 0 && values.checkIn && values.checkOut) {
				if (
					otherStays.some(
						(x) =>
							(x.checkIn >= values.checkIn && x.checkIn < values.checkOut) ||
							(x.checkOut > values.checkIn && x.checkOut <= values.checkOut) ||
							(x.checkIn >= values.checkIn && x.checkOut <= values.checkOut),
					)
				) {
					return t("The Check-in and check-out field must not overlap with other stays.");
				}
			}

			if (
				!!stayConfiguration.maxNights &&
				differenceInDays(values.checkOut, values.checkIn) > stayConfiguration.maxNights
			) {
				return t("A maximum of {{count}} nights can be booked.", { count: stayConfiguration.maxNights });
			}

			if (stayConfiguration.isPreferredHotelSelectable && values.requestedHotelId == null) {
				return t("A hotel must be selected.");
			}

			if (stayConfiguration.isPreferredRoomTypeSelectable && values.requestedRoomTypeId == null) {
				return t("A room must be selected.");
			}
		};

	return (
		<RegistrationFormContext.Provider
			value={{
				errors,
				registrationForm,
				previousStep,
				submitStep,
				validateStep,
				validateStaticStep,
				submitRegistration,
				submitLanding,
				filledSteps,
				givenConsents,
				companionsByType,
				saveCompanion,
				removeCompanion,
				hiddenSteps,
				getHiddenSections: _getHiddenSections,
				getHiddenDataFields: _getHiddenDataFields,
				isPreview: false,
				clearRegistration,
				declineRegistration,
				cancelRegistration,
				nominateReplacement,
				travels,
				setTravels,
				landingConsents,
				areConsentsLoading,
				companionFormValidation,
				validateCompanions,
				getStayConfigurationValidation,
				getHotelStayValidation,
				getHotelSummary: useHotelSummary,
				submitRegistrationFormDataForShopSummary,
				isShopEnabled: registrationForm.isShopEnabled,
				hasSeenLanding,
				setHasSeenLanding,
			}}
		>
			{children}
		</RegistrationFormContext.Provider>
	);
};

export const getDataFieldValuesToSubmit = (
	registrationForm: RegistrationFormResource,
	filledSteps: FilledSteps,
	formData: FormData,
) => {
	const dataFieldValues = flattenStepDataFields(filledSteps);
	const hiddenDataFields = getDataFieldsVisibility(
		registrationForm.steps,
		registrationForm.ruleSets,
		filledSteps,
		registrationForm.guest,
	);

	return registrationForm.steps
		.flatMap(getSectionsFromStep)
		.filter((section) => section.section.type === "default")
		.flatMap(({ section }) =>
			toGuestAttributeChildrenFromSection(section).filter(
				(guestAttribute) => !hiddenDataFields.includes(`${section.id}-${guestAttribute.dataField.id}`),
			),
		)
		.map(toDataField)
		.map(toDataFieldRequestValues(dataFieldValues, formData));
};

interface HotelSectionToSubmit {
	section: RegistrationFormSectionResourceTypeHotelSection;
	formValues: HotelSectionFormValues;
}

export const getHotelSectionsToSubmit = (
	registrationForm: RegistrationFormResource,
	filledSteps: FilledSteps,
): HotelSectionToSubmit[] => {
	const hiddenSections = getSectionsVisibility(
		registrationForm.steps,
		registrationForm.ruleSets,
		filledSteps,
		registrationForm.guest,
	);

	return registrationForm.steps.flatMap((step) => {
		return getSectionsFromStep(step)
			.filter((section) => section.section.type === "hotel" && !hiddenSections.includes(section.section.id))
			.map(({ section }) => section as RegistrationFormSectionResourceTypeHotelSection)
			.filter((section) => !!filledSteps[step.id].hotelSections?.[section.hotelSettings.hotelStayTypeId])
			.map((section) => ({
				section,
				formValues: filledSteps[step.id].hotelSections[section.hotelSettings.hotelStayTypeId],
			}));
	});
};
